import {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {buildDownloadFileName} from '@afriex/core/utils';

import {TRANSACTION_HISTORY_PAGE_SIZE} from '@/components/transactions/History';
import {transactionService} from '@/services/transactionService';
import type {AfriexAdminTransaction} from '@/types';
import {formatAdminTransaction} from '@/utils/dashboard';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {resolveTransactionProcessor} from '@/utils/transactionProcessor';

const useTransactionDetailsPage = () => {
  const params = useParams();
  const [transactionHistory, setTransactionHistory] = useState<
    AfriexAdminTransaction[]
  >([]);
  const [transactionDetail, setTransactionDetail] =
    useState<AfriexAdminTransaction>({} as AfriexAdminTransaction);

  const [loadingTnxDetail, setLoadingTnxDetail] = useState<boolean>(false);
  const [loadingTnxHistory, setLoadingTnxHistory] = useState<boolean>(false);
  // The customer whose history the page shows; both the history pages and
  // the export are scoped to it.
  const [historyUserId, setHistoryUserId] = useState<string>('');
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  // 0-indexed page the Recent History table is showing. Tracked
  // so the page handler can ignore the `onPageChange({page: 0})` the
  // Pagination footer fires on mount, and handed to the footer so its page
  // number follows a reload it did not start.
  const [historyPage, setHistoryPage] = useState<number>(0);
  // Only the latest history request may write state: an older page (or the
  // previous transaction's history) that resolves late must not replace it.
  const historyFetchIdRef = useRef<number>(0);

  const fetchTransactionHistory = useCallback(
    async (userId: string, page = 0): Promise<void> => {
      const fetchId = ++historyFetchIdRef.current;
      try {
        setLoadingTnxHistory(true);
        const response = await transactionService.getTransactionList({
          userId,
          page,
          limit: TRANSACTION_HISTORY_PAGE_SIZE,
        });
        if (fetchId !== historyFetchIdRef.current) {
          return;
        }
        setTransactionHistory(response.data);
        setTotalTransactions(response.total);
        setHistoryPage(page);
        setLoadingTnxHistory(false);
      } catch (_err) {
        if (fetchId === historyFetchIdRef.current) {
          setLoadingTnxHistory(false);
        }
      }
    },
    [],
  );

  /**
   * Load the page the footer asked for.
   *
   * `Pagination` calls `onPageChange` once on mount with page 0 as its own
   * state settles. The detail load already fetched page 0, so an unguarded
   * handler would refetch it on every open. Only a page that actually changed
   * hits the API, and only once the customer id is known.
   */
  const handleHistoryPageChange = useCallback(
    (pageParams: {limit: number; page: number}): Promise<void> => {
      if (pageParams.page === historyPage || !historyUserId) {
        return Promise.resolve();
      }
      return fetchTransactionHistory(historyUserId, pageParams.page);
    },
    [fetchTransactionHistory, historyPage, historyUserId],
  );

  const fetchTransactionDetails = useCallback(async () => {
    try {
      setLoadingTnxDetail(true);
      const initialTransaction = await transactionService.getTransaction({
        id: params.transactionId as string,
      });
      const currentTransaction =
        await resolveTransactionProcessor(initialTransaction);
      const filtered = await formatAdminTransaction(currentTransaction);
      setTransactionDetail({...currentTransaction, ...filtered});
      // Guarded on the id being present: a missing `sourceUserId` would
      // otherwise be serialized as the literal `userId=undefined`, which the
      // server rejects.
      const sourceUserId = currentTransaction?.sourceUserId as string;
      if (sourceUserId) {
        setHistoryUserId(sourceUserId);
        // Deliberately not awaited: the history load owns its own
        // `loadingTnxHistory` flag, and awaiting here would hold the detail
        // spinner open until the list lands. It catches its own failures, so
        // this only stops an unexpected throw becoming an unhandled rejection.
        fetchTransactionHistory(sourceUserId).catch(() => {});
      }
      setLoadingTnxDetail(false);
    } catch (_err) {
      setLoadingTnxDetail(false);
    }
  }, [params.transactionId, fetchTransactionHistory]);

  // Exports the same user's full history the Recent History panel lists, as
  // a server-streamed CSV. It used to export a prefetched first 100
  // transactions across every user instead.
  async function exportTransactionHistory(): Promise<void> {
    setLoadingExport(true);
    try {
      const blob = await transactionService.downloadTransactionList({
        userId: historyUserId,
      });
      saveBlob(
        blob,
        buildDownloadFileName('transactions', 'csv', historyUserId),
      );
    } catch (error) {
      showErrorMessage(getErrorMessage(error, 'Failed to export transactions'));
    } finally {
      setLoadingExport(false);
    }
  }

  function onExport(): void {
    // exportTransactionHistory reports its own failures, so this only stops
    // an unexpected throw becoming an unhandled rejection.
    exportTransactionHistory().catch(() => {});
  }

  useEffect(() => {
    fetchTransactionDetails().catch(() => {});
  }, [fetchTransactionDetails]);

  return {
    transactionDetail,
    transactionHistory,
    totalTransactions,
    loadingTnxDetail,
    loadingTnxHistory,
    loadingExport,
    onExport,
    historyPage,
    handleHistoryPageChange,
  };
};

export default useTransactionDetailsPage;
